import { IReport } from './base';
import { parseScaleValueIndex } from '../reportHelper';
import { shortQuestion } from '@arnold/core';

export type Statement = {
  text: string;
  comments: StatementComment[];
  answers: StatementAnswer[];
};

export type StatementComment = {
  text: string;
  author: string;
  teamName: string;
  teamId: string;
  optionIndex: number;
};

export type StatementAnswer = {
  optionIndex: number;
  count: number;
  teamId?: string;
};

type RespondentTeam = {
  teamId: string;
  teamName: string;
};

/**
 * Find the team for a given respondent
 * Checks both direct membership and team leadership
 */
const findRespondentTeam = (respondentId: string, report: IReport): RespondentTeam => {
  const respondent = report.respondents.find((r) => r.id === respondentId);
  let teamId = '';
  let teamName = '';

  if (respondent && report.teams) {
    // Check direct membership in teams
    const team = report.teams.find((team) => team.directMembers && team.directMembers.includes(respondent.id));

    if (team) {
      teamId = team.id || '';
      teamName = team.teamName || '';
    }

    // Check if respondent is a team leader
    if (!teamId) {
      const leadingTeam = report.teams.find((team) => team.teamLeader === respondent.id);
      if (leadingTeam) {
        teamId = leadingTeam.id || '';
        teamName = leadingTeam.teamName || '';
      }
    }
  }

  return { teamId, teamName };
};

/**
 * Process answers for a specific statement
 * Groups answers by team and counts occurrences of each option
 */
const processStatementAnswers = (
  statementMessages: NonNullable<IReport['responses']>,
  questionIndex: string,
  statementIndex: number,
  report: IReport,
): StatementAnswer[] => {
  const answers: StatementAnswer[] = [];

  if (!statementMessages || statementMessages.length === 0) {
    return answers;
  }

  // Count occurrences of each option per team
  const optionCountsByTeam = new Map<string, Map<number, number>>();

  statementMessages.forEach((response) => {
    response.messages?.forEach((message) => {
      if (
        message.questionDefinitionIndex === questionIndex &&
        message.statementIndex === statementIndex &&
        message.value != null
      ) {
        const parsedValue = parseScaleValueIndex(message.value);
        const optionIndex = parsedValue == null ? -1 : parsedValue;

        // Find respondent's team
        const respondent = report.respondents.find((r) => r.id === response.respondentId);
        let teamId = '';

        if (respondent && report.teams) {
          const { teamId: foundTeamId } = findRespondentTeam(response.respondentId, report);
          teamId = foundTeamId;
        }

        // Initialize team map if not exists
        if (!optionCountsByTeam.has(teamId)) {
          optionCountsByTeam.set(teamId, new Map<number, number>());
        }

        // Update count for this option in this team
        const teamCounts = optionCountsByTeam.get(teamId)!;
        teamCounts.set(optionIndex, (teamCounts.get(optionIndex) || 0) + 1);
      }
    });
  });

  // Convert to expected format
  optionCountsByTeam.forEach((teamCounts, teamId) => {
    teamCounts.forEach((count, optionIndex) => {
      answers.push({ optionIndex, count, teamId: teamId || undefined });
    });
  });

  return answers;
};

/**
 * Process comments for a specific statement
 * Associates comments with respondents and their teams
 */
const processStatementComments = (
  statementMessages: NonNullable<IReport['responses']>,
  questionIndex: string,
  statementIndex: number,
  report: IReport,
  anonymousLabel: string = 'Anonymous',
): StatementComment[] => {
  if (!statementMessages) {
    return [];
  }

  const commentsArray =
    statementMessages?.flatMap(
      (response) =>
        response.messages
          ?.filter(
            (message) =>
              message.questionDefinitionIndex === questionIndex &&
              message.statementIndex === statementIndex &&
              message.comment,
          )
          .map((message) => {
            // Find the respondent
            const respondent = report.respondents.find((r) => r.id === response.respondentId);

            // Find the team for this respondent
            const { teamId, teamName } = respondent
              ? findRespondentTeam(response.respondentId, report)
              : { teamId: '', teamName: '' };

            return {
              text: message.comment || '',
              author:
                respondent && !message.anonymous ? `${respondent.firstname} ${respondent.surname}` : anonymousLabel,
              teamName,
              teamId,
              optionIndex: parseScaleValueIndex(message.value) ?? -1,
            };
          }) || [],
    ) || [];

  return commentsArray;
};

/**
 * Process all statements for a question
 * Returns an array of Statement objects with answers and comments
 */
export const processStatements = (
  statementsLocal: string[],
  questionIndex: string,
  report: IReport,
  anonymousLabel: string = 'Anonymous',
): Statement[] => {
  return statementsLocal.map((statement, statementIndex) => {
    // Find messages for this statement
    const statementMessages = report.responses?.filter(
      (response) =>
        response.messages?.some(
          (message) => message.questionDefinitionIndex === questionIndex && message.statementIndex === statementIndex,
        ),
    );

    // Process answers for this statement
    const answers = processStatementAnswers(statementMessages || [], questionIndex, statementIndex, report);

    // Process comments for this statement
    const comments = processStatementComments(
      statementMessages || [],
      questionIndex,
      statementIndex,
      report,
      anonymousLabel,
    );

    return {
      text: shortQuestion(statement),
      comments,
      answers,
    };
  });
};
